<template>
    <div class="ls-customer-view--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="customer">
            <div v-if="atMostOneChildBelowCustomerView" class="ls-customer-view--customer">
                <LsCustomerDetails :customer="customer" />
            </div>
            <BTabs
                v-if="atMostOneChildBelowCustomerView"
                v-model="activeTab"
                type="is-boxed"
                @change="onChangeTab"
            >
                <BTabItem label="Overview"></BTabItem>
                <BTabItem label="Billing"></BTabItem>
            </BTabs>

            <div v-if="!selectedProperty && activeTab === 0">
                <div class="ls-customer-view--dropdowns">
                    <div class="ls-customer-view--dropdowns-address">
                        <span>Address:</span>
                        <LsDropdownProperties
                            v-model="selectedProperty"
                            :properties="customer.properties"
                            :can-add="!isAccountPendingCompletion"
                            track-id="customer_view-address"
                            @input="onChangeProperty"
                        />
                    </div>
                    <div class="ls-customer-view--dropdowns-service-type">
                        <span>Service Type:</span>
                        <LsDropdownServices
                            value
                            :services="[]"
                            track-id="customer_view-service_type"
                        />
                    </div>
                </div>

                <LsContentPlaceholder
                    :message="
                        isAccountPendingCompletion
                            ? 'New properties and services cannot be added until the order finishes processing.'
                            : 'Select Address'
                    "
                    :image="isAccountPendingCompletion ? 'inactive' : 'select'"
                />
            </div>
            <router-view />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsRouterHelpers } from 'ls-vue-common';
import { tabConstants } from './tabs.constants';
import LsContentPlaceholder from '@/components/LsContentPlaceholder/LsContentPlaceholder.vue';
import LsCustomerDetails from '@/components/LsCustomerDetails/LsCustomerDetails.vue';
import LsDropdownProperties from '@/components/LsDropdown/LsDropdownProperties.vue';
import LsDropdownServices from '@/components/LsDropdown/LsDropdownServices.vue';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import Customer from '@/graphql/queries/Customer.gql';

const customerTabs = {
    customer: 0,
    property: 0,
    billing: 1,
};

export default {
    name: 'CustomerView',
    components: {
        LsContentPlaceholder,
        LsCustomerDetails,
        LsDropdownProperties,
        LsDropdownServices,
        LsTemplatedLoading,
    },
    data() {
        return {
            customer: {},
            loading: 0,
        };
    },
    computed: {
        atMostOneChildBelowCustomerView() {
            return this.$route.matched.length <= 4;
        },
        propertiesById() {
            return _.keyBy(this.customer.properties, 'id');
        },
        isAccountPendingCompletion() {
            return this.customer.is_order_processing;
        },
        selectedProperty: {
            get() {
                return _.toString(this.$route.params.property_id);
            },
            set(value) {
                return value;
            },
        },
        isOverview() {
            return this.activeTab === 0;
        },
        shouldRouteToPropertyView() {
            return (
                _.size(this.customer.properties) === 1 &&
                this.isOverview &&
                this.name === 'customer'
            );
        },
        name() {
            return lsRouterHelpers.getName(this.$route);
        },
        activeTab: {
            get() {
                return customerTabs[this.name];
            },
            set(value) {
                return value;
            },
        },
    },
    methods: {
        onChangeProperty(property_id) {
            if (this.isAccountPendingCompletion) {
                return;
            }

            if (property_id === '0') {
                return this.$router.push({
                    name: 'addProperty',
                    params: {
                        customer_id: this.$route.params.customer_id,
                        property_id: this.selectedProperty,
                    },
                });
            }
            this.$router.push({
                name: 'property',
                params: {
                    customer_id: this.$route.params.customer_id,
                    property_id,
                },
            });
        },
        onChangeTab(idx) {
            if (idx === this.activeTab) {
                // Prevent navigating when changing tab matches active tab
                // This happens due to an error with apollo updating twice
                // https://github.com/vuejs/vue-apollo/issues/210
                return null;
            }

            if (tabConstants[idx] === 'Overview') {
                if (_.size(this.customer.properties) === 1) {
                    this.$router.push({
                        name: 'property',
                        params: {
                            customer_id: this.$route.params.customer_id,
                            property_id: this.customer.properties[0].id,
                        },
                    });
                } else {
                    this.$router.push({
                        name: 'customer',
                        params: {
                            customer_id: this.$route.params.customer_id,
                        },
                    });
                }
            }

            if (tabConstants[idx] === 'Billing') {
                this.$router.push({
                    name: 'billing',
                    params: {
                        customer_id: this.$route.params.customer_id,
                    },
                });
            }
        },
    },
    apollo: {
        customer: {
            query: Customer,
            variables() {
                return {
                    id: _.toNumber(this.$route.params.customer_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    this.customer = data.customer;
                    const { properties } = data.customer;
                    if (this.shouldRouteToPropertyView) {
                        this.$router.push({
                            name: 'property',
                            params: {
                                customer_id: this.$route.params.customer_id,
                                property_id: properties[0].id,
                            },
                        });
                    }
                }
            },
            error(error) {
                lsErrorService.log({ error });
            },
            loadingKey: 'loading',
        },
    },
};
</script>

<style lang="scss">
.ls-customer-view--customer {
    margin-bottom: 24px;
}

.ls-customer-view--dropdowns {
    display: flex;
    width: 760px;
    margin-bottom: 20px;

    div {
        flex: 1;
        display: flex;
        align-items: center;
    }

    span {
        margin-right: 10px;
        color: $secondary;
    }

    .ls-customer-view--dropdowns-address {
        margin-right: 20px;
    }

    .dropdown-content {
        flex-direction: column;
    }

    .ls-new-property--add {
        display: flex;
        flex: initial;
        justify-content: center;
        align-items: center;
        background-color: $primary;
        color: $white;
        border-radius: 50%;
        height: 20px;
        width: 20px;
        font-size: $size-2;
        padding-left: 1px;
        margin-right: 8px;
    }
}

.ls-customer-view--container {
    .b-tabs .tabs {
        margin-bottom: 24px;

        ul {
            border-bottom: 1px dashed $secondary;
        }

        li > a {
            color: $primary;
            border-bottom: 0;
        }

        .is-active {
            border-bottom: 0;

            a {
                color: $black;
                border-bottom: 0;
            }
        }
    }

    .tabs.is-boxed li.is-active a {
        background-color: $grey-lighter;
        border: 1px dashed $secondary;
        border-bottom-color: transparent !important;
    }

    .b-tabs .tab-content {
        padding: 0;
    }

    .tabs.is-boxed a {
        border-radius: 0;
    }

    .b-tabs:not(:last-child) {
        margin-bottom: 0;
    }

    .tabs.is-boxed a:hover {
        background-color: inherit;
    }

    .tabs.is-boxed .is-active a:hover {
        opacity: 1;
    }
}
</style>
